import React, { useState, useContext } from "react";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisEmptyList from "../../components/controls/emptylist";
import CaseDetails from "../../components/core/caseDetails";
import JisDialogForm from "../../components/formdialog";
import "../../styles/bail-case-search.css";
import { ChevronLeft } from "lucide-react";

// Bail Case Search: find open cases by party name (first, middle, last name or alias).
// Several names can be separated by commas.
const MIN_LENGTH = 3;

const personName = (p, fallbackSuffix = "") =>
  p ? `${p.suffix || fallbackSuffix} ${p.firstName || ""} ${p.lastName || ""}`.trim() : "N/A";

function BailCaseSearch() {
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState({ totalCount: 0, truncated: false, fuzzy: false });
  const [loading, setLoading] = useState(false);
  const [partyName, setPartyName] = useState("");
  const [searchedFor, setSearchedFor] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);
  const [openCase, setOpenCase] = useState(false);

  const onSearchCases = async () => {
    const name = partyName.trim();
    const parts = name.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0 || parts.some((s) => s.replace(/\s/g, "").length < MIN_LENGTH)) {
      setErr(`Please enter a party name of at least ${MIN_LENGTH} characters`);
      return;
    }

    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(
        `${Constants.apiUrl}/hearing/get-related-cases?partyname=${encodeURIComponent(name)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await resp.json();
      if (data.code === 0) {
        setCases(data.results || []);
        setMeta({
          totalCount: data.totalCount ?? (data.results || []).length,
          truncated: !!data.truncated,
          fuzzy: !!data.fuzzy,
        });
        setSearchedFor(name);
        setHasSearched(true);
      } else if (data.code === -10) {
        setIsExpired(true);
      } else {
        // validation errors keep the user on the search box
        setErr(data.status || data.message || "An error occurred");
      }
    } catch (error) {
      console.error(error);
      setErr(Constants.serverErr || "Server error occurred");
    } finally {
      setLoading(false);
    }
  };

  const onCloseOpenCase = () => {
    setActive(null);
    setOpenCase(false);
  };

  const onOpenCase = (kase) => {
    setActive(kase);
    setOpenCase(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") onSearchCases();
  };

  const handleBackToSearch = () => {
    setHasSearched(false);
    setCases([]);
    setErr("");
  };

  return (
    <div>
      {!hasSearched ? (
        <div className="search-container">
          <h2 className="search-title">Search For Bail Cases</h2>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input-large"
              placeholder="Search by party name, e.g. Lolade Ashimolowo"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={200}
            />
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, textAlign: "center", marginTop: 6 }}>
            Searches first, middle and last names and aliases of parties on open cases. Separate
            several names with commas.
          </div>
          <div className="search-button-wrapper">
            <JisButton onClick={onSearchCases} variant="primary">
              Search
            </JisButton>
          </div>
          {err && <div className="error-message">{err}</div>}
        </div>
      ) : (
        <div className="table">
          <div style={{ marginBottom: "20px" }}>
            <button className="case-search-back-button" onClick={handleBackToSearch}>
              <ChevronLeft size={24} /> <span>Back</span>
            </button>
          </div>
          <div className="section-cases">
            <span>
              Verify Bail Case - &quot;{searchedFor}&quot; ({meta.totalCount} {meta.totalCount === 1 ? "result" : "results"})
            </span>
          </div>
          {meta.fuzzy && cases.length > 0 && (
            <div style={{ fontSize: 12, margin: "6px 0" }}>
              No exact match was found. Showing parties with similar names.
            </div>
          )}
          {meta.truncated && (
            <div style={{ fontSize: 12, margin: "6px 0" }}>
              Showing the best {cases.length} matches. Type a fuller name to narrow the results.
            </div>
          )}
          <div className="table-wrapper">
            {cases?.length > 0 ? (
              <table className="case-table">
                <thead>
                  <tr>
                    <th className="col-title">Suit Number</th>
                    <th className="col-title">Case Title</th>
                    <th className="col-title">Matched Party</th>
                    <th className="col-medium">Date Initiated</th>
                    <th className="col-medium">Filing Type</th>
                    <th className="col-medium">Classification</th>
                    <th className="col-title">Counsel</th>
                    <th className="col-medium">Assigned To</th>
                    <th className="col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((opt) => {
                    const category = Constants.getItemById(frequentData?.casecategories, opt.caseCategoryId);
                    return (
                      <tr
                        key={opt.caseId}
                        onClick={() => onOpenCase(opt)}
                        style={{ cursor: "pointer" }}
                        title="View case details"
                      >
                        <td style={{ backgroundColor: category?.colour || "#ffffff" }}>
                          {opt.caseNumber || opt.referenceNumber || "N/A"}
                        </td>
                        <td>{opt.caseTitle || "N/A"}</td>
                        <td>
                          {(opt.parties || []).map((p) => (
                            <div key={p.partyId}>
                              {p.name}
                              {p.alias ? ` (${p.alias})` : ""}
                            </div>
                          ))}
                        </td>
                        <td>
                          {opt.createdOn
                            ? Constants.momentDate(opt.createdOn).format("DD MMM, YYYY")
                            : "N/A"}
                        </td>
                        <td>
                          {Constants.getItemById(frequentData?.casetype, opt.caseTypeId)?.caseType1 || "N/A"}
                        </td>
                        <td>{category?.categoryTitle || "N/A"}</td>
                        <td>{personName(opt.legalCounsel)}</td>
                        <td>{personName(opt.judge, "Hon. Justice")}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <JisButton
                            onClick={(e) => {
                              e?.stopPropagation?.();
                              onOpenCase(opt);
                            }}
                          >
                            View Details
                          </JisButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <JisEmptyList title="No open cases found for this party name" />
            )}
          </div>
        </div>
      )}

      <BackLoader open={loading} />

      {openCase && active != null && (
        <JisDialogForm
          title={`Case Details - ${active.caseNumber || active.referenceNumber || ""}`}
          onClose={onCloseOpenCase}
          minWidth="80%"
        >
          <div>
            <CaseDetails caseId={active.caseId} data={null} token={token} />
          </div>
        </JisDialogForm>
      )}
    </div>
  );
}

export default BailCaseSearch;
